import { jsPDF } from 'jspdf';
import autoTable, { type PageHook } from 'jspdf-autotable';
import { TRAVELERS_LOGO_BASE64 } from '../../assets/trav-logo-base64';
import type { Vendor, PropertyVendor } from '../../types/providerSearch';
import store, { type RootState } from '../../redux/store';
import { getFullStateName } from './exportHelpers';
import { searchConfig } from '../../config/searchConfig';
import { 
  
  getAllPropertyVendorSpecialtyOptions 
} from '../../config/updateProviderConfig';
import { CONTRACT_TYPE_OPTIONS } from '../../constants/providerSearchConstants';
import { DISCLAIMER, AUTO_SEARCH_TEXT } from '../../constants';
//import { STATUS_OPTIONS, SERVICE_CODE_OPTIONS_PROPERTY_GROUPED } from '../../constants/providerSearchConstants';

// Modified interface to make vendorId optional
export interface VendorData {
  vendorName?: string;
  name?: string;
  vendorId?: string; // Made optional to accommodate ContractVendor
  id?: string;       // Added as alternative identifier
  address: string | any; // Updated to allow for object type
  phone: string;
  status: string;
  approximateMiles?: string;
  isSelected?: boolean;
  actions?: any;
  [key: string]: any;
}

export interface ExportToPDFOptions {
  title?: string;
  filename?: string;
  pageSize?: string;
  orientation?: 'portrait' | 'landscape';
  rowsPerPage?: number;
  showPageNumbers?: boolean;
  userName?: string;
  isLocalSearch?: boolean;
  externalSearchType?: 'local' | 'name' | 'region';
}

/**
 * Formats address data into a readable string
 * @param address - Address data which could be a string, object, or JSX element
 * @returns Formatted address string
 */
const formatAddress = (address: any): string => {
  if (!address) return 'N/A';

  // If address is a string, return it directly
  if (typeof address === 'string') return address;

  // If address is a JSX element (has props)
  if (address && typeof address === 'object' && address.props) {
    // Try to extract text from JSX children
    try {
      const children = address.props.children;
      if (Array.isArray(children)) {
        // Extract street from first child
        const street = children[0] || '';
        // Extract city, state, zip from third child (after <br/>)
        const locationParts = children[2] || '';
        if (typeof locationParts === 'string') {
          return `${street}, ${locationParts}`;
        }
      }
    } catch (e) {
      console.error('Error extracting address from JSX:', e);
    }
    return 'See details in application';
  }

  // If address is a regular object with address properties
  if (address && typeof address === 'object') {
    const street = address.street || '';
    const city = address.city || '';
    const state = address.state || address.stateCode || ''; // Added stateCode as fallback
    const zipCode = address.zipCode || address.zip || ''; // Added zip as fallback

    // Special handling for "*Available" addresses (statewide, nationwide, etc.) with no city/zip
    if (street.includes('*Available') && !city && !zipCode) {
      return `${street} ${state}`.trim();
    }

    const formattedAddress = `${street}, ${city}, ${state} ${zipCode}`.trim();
    return formattedAddress !== ', ,  ' ? formattedAddress : 'N/A';
  }

  // Fallback
  return 'N/A';
};

const joinContractDetailField = (contractDetails: Array<any> | undefined, field: string): string => {
  if (!contractDetails || !Array.isArray(contractDetails) || contractDetails.length === 0) return '';
  return contractDetails.map(cd => (cd && cd[field]) ? String(cd[field]).trim() : '').filter(Boolean).join(', ');
};

// Helper to loop vendor array and produce joined contract detail field values per vendor
const mapContractFieldFromVendors = (vendorData: Array<any> | undefined, field: string): string[] => {
  if (!vendorData || !Array.isArray(vendorData) || vendorData.length === 0) return [];
  return vendorData.map(v => joinContractDetailField((v as any).contractDetails, field));
};

/**
 * Helper function to extract state and zip from title
 * @param title - The title string that may contain state and zip
 * @returns Object with state code and zip
 */
const extractLocationFromTitle = (title: string): { stateCode: string, zip: string } => {
  // Try to extract state and zip from format like "Search Results: "MN, 55401""
  const stateZipMatch = title.match(/(?:Search Results|Vendors For): "([A-Z]{2}),?\s+(\d{5})"/);
  if (stateZipMatch && stateZipMatch[1] && stateZipMatch[2]) {
    return { stateCode: stateZipMatch[1], zip: stateZipMatch[2] };
  }
  
  // Try to extract state from format like "Search Results: "Vendor Name, MN""
  const stateMatch = title.match(/(?:Search Results|Vendors For): ".*?,\s+([A-Z]{2})"/);
  if (stateMatch && stateMatch[1]) {
    return { stateCode: stateMatch[1], zip: '' };
  }
  
  return { stateCode: '', zip: '' };
};



/**
 * Transforms the original title to replace "Search Results" with "Vendors For"
 * @param title - Original title string
 * @returns Modified title string
 */
const transformTitle = (title: string): string => {
  return title.replace(/Search Results/g, 'Vendors For');
};

// Cache for specialty code mappings

let propertySpecialtyMap: Map<string, string> | null = null;

/**
 * Get specialty description from code for contract vendors
 * @param code - Specialty code
 * @returns Specialty description
 */


/**
 * Get specialty description from code for property vendors
 * @param code - Specialty code
 * @returns Specialty description
 */
const getPropertySpecialtyDescription = async (code: string): Promise<string> => {
  if (!code) return '';
  
  // Initialize map if not already done
  if (!propertySpecialtyMap) {
    const options = await getAllPropertyVendorSpecialtyOptions();
    propertySpecialtyMap = new Map(options.map(opt => [opt.id, opt.value]));
  }
  
  return propertySpecialtyMap.get(code) || code;
};

/**
 * Convert specialty codes to descriptions
 * @param codes - Comma-separated specialty codes
 * @param vendorType - Type of vendor (contract, property, etc)
 * @returns Comma-separated specialty descriptions
 */
const mapSpecialtyCodesToDescriptions = async (
  codes: string | string[] | null | undefined,
  vendorType: string
): Promise<string> => {
  if (!codes) return '';
  
  
  let codeArray: string[];

  if (Array.isArray(codes)) {
    codeArray = codes.map(c => String(c).trim()).filter(Boolean);
  } else if (typeof codes === 'string') {
    codeArray = codes
      .split(',')
      .map(c => c.trim())
      .filter(Boolean);
  } else {
    // Fallback for unexpected types
    codeArray = [String(codes).trim()].filter(Boolean);
  }

  if (codeArray.length === 0) return '';

  
  if (vendorType === 'property' ) {
    const descriptions = await Promise.all(
      codeArray.map(code => getPropertySpecialtyDescription(code))
    );
    return descriptions.join(', ');
  }
  
   // If vendorType not recognized, just return the raw codes
  return codeArray.join(', ');
};

/**
 * Convert contract type codes to descriptions
 * PR and PF both map to "Procurement form", SF maps to "Short form"
 * @param codes - Comma-separated contract type codes
 * @returns Comma-separated contract type descriptions
 */
const getContractTypeDescriptions = (codes: string): string => {
  if (!codes) return '';
  
  const codeArray = codes.split(',').map(c => c.trim()).filter(Boolean);
  const descriptions = codeArray.map(code => {
    // Handle PF|PR combined code
    if (code === 'PF|PR') {
      const option = CONTRACT_TYPE_OPTIONS.find(opt => opt.id === 'PF');
      return option ? option.value : code;
    }
    const option = CONTRACT_TYPE_OPTIONS.find(opt => opt.id === code);
    return option ? option.value : code;
  }).filter(Boolean);
  
  return descriptions.join(', ');
};

/**
 * Normalizes vendor data to ensure it has the required fields for PDF export
 * @param vendorData - Array of vendor data objects
 * @returns Normalized vendor data array
 */
const normalizeVendorData = <T extends Vendor |PropertyVendor >(
  vendorData: T[]
): VendorData[] => {
  // Precompute per-vendor joined contract detail strings
  const ownerNamesArr = mapContractFieldFromVendors(vendorData as Array<any>, 'contractOwnerName');
  const typesArr = mapContractFieldFromVendors(vendorData as Array<any>, 'contractType');

  return vendorData.map((vendor, idx) => {

    // Create the normalized vendor object with basic properties
    const normalizedVendor: VendorData = {
      name: vendor.name || '',
      vendorId: 'vendorId' in vendor && vendor.vendorId ? vendor.vendorId : 
                'id' in vendor && vendor.id ? vendor.id : '',
      address: vendor.address || '',
      phone: vendor.phone || '',
      status: vendor.status || '',
    };
    
    // Handle approximateMiles
    if ('approximateMiles' in vendor) {
      normalizedVendor.approximateMiles = String(vendor.approximateMiles);
    }
    
    // Handle vendor specialty
    if ('vendorSpecialty' in vendor) {
      normalizedVendor.vendorSpecialty = vendor.vendorSpecialty;
    } else if ('vendorSpeciality' in vendor) {
      normalizedVendor.vendorSpecialty = (vendor as any).vendorSpeciality;
    } else if ('specialty' in vendor) {
      normalizedVendor.vendorSpecialty = (vendor as any).specialty;
    }
    
    // Set contract type and owner from precomputed arrays when present
    const contractTypeForVendor = typesArr[idx] || '';
    if (contractTypeForVendor) normalizedVendor.contractType = contractTypeForVendor;

    const contractOwnerForVendor = ownerNamesArr[idx] || '';
    if (contractOwnerForVendor) normalizedVendor.contractOwner = contractOwnerForVendor;
    
    return normalizedVendor;
  });
};

/**
 * Exports vendor table data to PDF format
 * @param vendorData - Array of vendor data objects to be exported
 * @param options - Configuration options for the PDF export
 */
export const exportVendorsToPDF = async <T extends Vendor  | PropertyVendor >(
  vendorData: T[],
  options: ExportToPDFOptions = {}
): Promise<void> => {
  // Normalize vendor data to ensure it has the required fields
  const normalizedVendorData = normalizeVendorData(vendorData);
  
  const {
    title = 'Vendor Search Results',
    pageSize = 'a4',
    orientation = 'portrait',
    showPageNumbers = true,
    isLocalSearch = false,
  } = options;

  const state: RootState = store.getState();
  const vendorType = state.providerSearch.currentSearchCategory || 'auto';
  const externalSearchTypeState = (state.providerSearch as any).externalSearchType as ('local' | 'name' | 'region' | undefined);
  const effectiveSearchType: 'local' | 'name' | 'region' | undefined = options.externalSearchType ? options.externalSearchType : externalSearchTypeState;

  // Determine vendor type for title and orientation
  let vendorTypeTitle = vendorType === 'auto' ? 'Auto Repair Shops' : "";
  let pdfOrientation = orientation;
  const isContractVendor = false;
  
  if (vendorData.length > 0) {
    if (vendorType === 'property') {
      vendorTypeTitle = 'Property Vendors';
      pdfOrientation = 'landscape'; // Set landscape orientation for property vendors
    } 
  }

  const doc = new jsPDF({
    orientation: pdfOrientation, // Use the determined orientation
    unit: 'mm',
    format: pageSize,
  });

  const now = new Date();
  const dateTimeString = now.toLocaleString();
  
  // Extract state and zip from title if possible
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { stateCode, zip } = extractLocationFromTitle(title);
  const stateName = getFullStateName(stateCode);

  // Create first page with large logo and title
  try {
    // Add large Travelers logo at the top center of the first page
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const logoWidth = 100; // Larger logo for first page
    const logoHeight = 20; // Proportional height
    const logoX = (pageWidth - logoWidth) / 2 + 5; // Center horizontally with slight right offset
    const centerY = pageHeight / 2; // Center vertically
    
    doc.addImage(TRAVELERS_LOGO_BASE64, 'PNG', logoX, centerY - 40, logoWidth, logoHeight);
    
    // Add vendor type title
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(`All ${vendorTypeTitle}`, pageWidth / 2, centerY - 10, { align: 'center' });
    
    // Add state name if available
    if (stateName) {
      doc.setFontSize(18);
      doc.setFont('helvetica', 'normal');
      doc.text(stateName, pageWidth / 2, centerY + 2, { align: 'center' });
    }
    
    // Add disclaimer with large gap
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    
    // Position disclaimer at the bottom of the page with large gap
   if (vendorType === 'property') {
    doc.text(AUTO_SEARCH_TEXT.PROPERTY_DISCLAIMER, pageWidth / 2, pageHeight - 50, { align: 'center', maxWidth: pageWidth - 40 });
    } else {
    doc.text(DISCLAIMER.AUTO, pageWidth / 2, pageHeight - 50, { align: 'center' });
    }
    
    // Add export date and time on first page
    doc.setFontSize(8);
    doc.text(`Export Date: ${dateTimeString}`, pageWidth - 60, 10);
    
    // Add page number on first page
    doc.setFontSize(8);
    
    // Add a new page for the table content
    doc.addPage();
    
  } catch (error) {
    console.error('Error creating first page:', error);
  }

  // Define page hook for second page onwards
  let pageHook: PageHook | undefined = undefined;

if (showPageNumbers) {
  pageHook = function (_data) {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    try {
      // Header logo
      doc.addImage(TRAVELERS_LOGO_BASE64, 'PNG', 14, 10, 50, 10);
    } catch (error) {
      console.error('Error adding logo to PDF:', error);
    }

    // Export date/time
    doc.setFontSize(8);
    doc.text(`Export Date: ${dateTimeString}`, pageWidth - 60, 10);

    // Disclaimer at bottom
    doc.setFontSize(8);
    
    // Position disclaimer at the bottom of the page with large gap
    if (vendorType === 'property') {
    doc.text(AUTO_SEARCH_TEXT.PROPERTY_DISCLAIMER, pageWidth / 2, pageHeight - 25, { align: 'center', maxWidth: pageWidth - 40 });
    } else {
    doc.text(DISCLAIMER.AUTO, pageWidth / 2, pageHeight - 25, { align: 'center' });
    }
  };
}


  // Define columns based on vendor type
  let tableColumns: string[];
  let useConfigColumns = false;
  let activeColumnsConfig: Array<{ label: string; field?: string; type?: 'actions' }>|undefined;
  
 if (vendorType === 'property') {
      // Derive columns from searchConfig based on mode (external) and search type (local/name)
      const categoryConfig = (searchConfig as any)[vendorType];
      if (categoryConfig) {
        // Prefer external configuration (this export is used by external UI)
        // Fall back to admin or direct mapping if needed
        const externalCfg = categoryConfig.external || categoryConfig.admin || categoryConfig;
        const searchKey = effectiveSearchType || 'local';
        if (externalCfg && externalCfg[searchKey] && externalCfg[searchKey].columns) {
          activeColumnsConfig = externalCfg[searchKey].columns;
        }
      }
    if (activeColumnsConfig) {
      // Exclude actions columns for PDF
      tableColumns = activeColumnsConfig.filter(c => c.type !== 'actions').map(c => c.label);
      useConfigColumns = true;
    } else {
      // Fallback columns
      tableColumns = ['Vendor', 'Address', 'Phone'];
    }
  } else if (isLocalSearch) {
    // For local searches (auto vendors), include the miles column
    tableColumns = ['Vendor Name', 'Address', 'Approximate Miles', 'Phone'];
  } else {
    // For non-local searches (auto vendors)
    tableColumns = ['Vendor Name', 'Address', 'Phone'];
  }

  // Format the data for the table, handling address objects properly
  // Map specialty codes to descriptions for all vendors first
  const dataWithDescriptions = await Promise.all(
   (useConfigColumns ? vendorData : normalizedVendorData).map(async (vendor: any) => {
    if (isContractVendor) {
      // Check all possible specialty field names for contract vendors
      const specialtyCode = vendor.vendorSpecialty || vendor.vendorSpeciality || vendor.specialty;
      if (specialtyCode) {
        const description = await mapSpecialtyCodesToDescriptions(specialtyCode, 'contract');
        return { ...vendor, vendorSpecialtyDescription: description };
      }
     } else if ((vendorType === 'property'  )) {
      // Property vendors may have specialty in vendorSpecialty, vendorSpecializations, or serviceCodes
      const specialtyCode = vendor.vendorSpecialty || vendor.vendorSpecializations || vendor.serviceCodes;
      if (specialtyCode) {
        const description = await mapSpecialtyCodesToDescriptions(specialtyCode, 'property');
        return { ...vendor, vendorSpecialtyDescription: description };
      }
    }
    return vendor;
  })
);

  const tableRows = dataWithDescriptions.map((vendor: any) => {    
    if (isContractVendor) {
      // Extract contract type codes from contractDetails array (like SearchResults does)
      let contractTypeCodes = '';
      if ('contractType' in vendor && vendor.contractType) {
        contractTypeCodes = vendor.contractType;
      } else {
        const contractDetails = vendor.contractDetails;
        if (Array.isArray(contractDetails)) {
          contractTypeCodes = contractDetails
            .map((detail: any) => detail.contractType)
            .filter(Boolean)
            .join(', ');
        }
      }
      
      // Extract contract owner names from contractDetails array
      let contractOwnerNames = '';
      if (vendor.contractOwnerName) {
        contractOwnerNames = vendor.contractOwnerName;
      } else {
        const contractDetails = vendor.contractDetails;
        if (Array.isArray(contractDetails)) {
          contractOwnerNames = contractDetails
            .map((detail: any) => detail.contractOwnerName)
            .filter(Boolean)
            .join(', ');
        }
      }
      
      // For contract vendors
      if (useConfigColumns && activeColumnsConfig) {
        return activeColumnsConfig
          .filter(c => c.type !== 'actions')
          .map(c => {
            const field = c.field || '';
            switch (field) {
              case 'vendorName':
                return vendor.name || vendor.providerName || vendor.vendorName || '';
              case 'address':
                // Match SearchResults format: *Available Statewide STATE
                return `*Available Statewide ${vendor.address?.state || vendor.state || ''}`;
              case 'vendorSpeciality':
                return vendor.vendorSpecialtyDescription || vendor.vendorSpecialty || vendor.vendorSpeciality || '';
              case 'contractType':
                return getContractTypeDescriptions(contractTypeCodes);
              case 'contractOwnerName':
                return contractOwnerNames || '';
              default:
                return field && vendor[field] !== undefined ? String(vendor[field]) : '';
            }
          });
      }
      // Fallback if no config
      return [
        vendor.name || vendor.providerName || vendor.vendorName || '',
        `*Available Statewide ${vendor.address?.state || vendor.state || ''}`,
        vendor.vendorSpecialtyDescription || vendor.vendorSpecialty || '',
        getContractTypeDescriptions(contractTypeCodes),
        contractOwnerNames || '',
      ];

    } else if (vendorType === 'property') {
      // Property mapping
      if (useConfigColumns && activeColumnsConfig) {
        return activeColumnsConfig
          .filter(c => c.type !== 'actions')
          .map(c => {
            const field = c.field || '';
            switch (field) {
              case 'vendorName':
                return (vendor as any).doingBusinessAs || ('dba' in vendor && vendor.dba ? vendor.dba : vendor.name || vendor.vendorName || '');
              case 'address':
                return formatAddress(vendor.address);
              case 'approximateMiles':
              case 'Approx. Miles':
                return vendor.approximateMiles || '';
              case 'phone':
                return vendor.phone || '';
              case 'status':
                return (vendor as any).status || (vendor as any).status || '';
              case 'vendorSpecializations':
                // Use mapped specialty description if available; else raw serviceCodes
                return vendor.vendorSpecialtyDescription || (vendor as any).vendorSpecializations || (vendor as any).serviceCodes || '';
              case 'notes':
                return vendor.notes || '';
              default:
                return field && vendor[field] !== undefined ? String(vendor[field]) : '';
            }
          });
      }
      return [
        (vendor as any).doingBusinessAs || vendor.name || vendor.vendorName || '',
        formatAddress(vendor.address),
        vendor.phone || ''
      ];

    }  else if (isLocalSearch) {
      // For local searches (auto vendors), include the miles column
      return [
        vendor.name || vendor.providerName || vendor.vendorName || '',
        formatAddress(vendor.address),
        vendor.approximateMiles || '',
        vendor.phone || '',
      ];
    } else {
      // For non-local searches (auto vendors)
      return [
        vendor.name || vendor.providerName || vendor.vendorName || '',
        formatAddress(vendor.address),
        vendor.phone || '',
      ];
    }
  });

  // Define column styles based on vendor type
  let columnStyles = {} as any;
  
  if (isContractVendor && useConfigColumns) {
    // For contract vendors with dynamic columns (landscape orientation)
    // Let autotable size columns automatically
    columnStyles = {};
  } else if (isContractVendor) {
    // For contract vendors fallback (landscape orientation)
    columnStyles = {
      0: { cellWidth: 50 },  // Vendor Name
      1: { cellWidth: 70 },  // Address
      2: { cellWidth: 50 },  // Specialty
      3: { cellWidth: 40 },  // Contract Type
      4: { cellWidth: 40 },  // Contract Owner
    };
  } else if (useConfigColumns) {
    // Let autotable size columns automatically for dynamic property columns
    columnStyles = {};
  } else if (isLocalSearch) {
    // For local searches (auto vendors)
    columnStyles = {
      0: { cellWidth: 40 }, // Vendor Name
      1: { cellWidth: 60 }, // Address
      2: { cellWidth: 35 }, // Approximate Miles
      3: { cellWidth: 30 }, // Phone
    };
  } else {
    // For non-local searches (auto vendors)
    columnStyles = {
      0: { cellWidth: 50 }, // Vendor Name
      1: { cellWidth: 80 }, // Address
      2: { cellWidth: 35 }, // Phone
    };
  }

  // Use autoTable once to handle all rows - it will automatically create pages as needed
  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    // Reserve space for logo + title on every table page
    margin: { top: 40, left: 25, bottom: 40 },
    theme: 'grid',
    styles: {
      fontSize: 9.5,
      cellPadding: 3,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [51, 51, 51],
      textColor: 255,
      fontStyle: 'bold',
    },
    columnStyles: columnStyles,
    rowPageBreak: 'avoid',          // <<< prevent splitting a row across pages
    ...(pageHook ? { didDrawPage: pageHook } : {}),
    showHead: 'everyPage',
  });

  // After autoTable has finished, we know the real total page count
const totalPages = doc.getNumberOfPages();

// Replace the {totalPages} placeholder on every page
for (let i = 1; i <= totalPages; i++) {
  doc.setPage(i);
  const pageHeight = doc.internal.pageSize.height;

  // Re-draw the page number text at the same position, now with the real total
  // For cover page and table pages we used the same Y positions:
  const isCoverPage = i === 1;
  const y = isCoverPage ? pageHeight - 10 : pageHeight - 10;
  const x = 14; // same as you used in cover, and data.settings.margin.left in table

  doc.setFontSize(8);
  doc.text(`Page ${i} of ${totalPages}`, x, y);
}

  // Instead of downloading, open PDF in a new tab
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};


/**
 * Helper function to export only selected vendors to PDF
 * @param vendorData - Array of vendor data objects
 * @param options - Configuration options for the PDF export
 */
export const exportSelectedVendorsToPDF = async <T extends Vendor  | PropertyVendor>(
  vendorData: T[],
  options: ExportToPDFOptions = {}
): Promise<void> => {
  if (vendorData.length === 0) {
    return;
  }

  // Determine if this is a local search by checking if any vendor has approximateMiles
  const isLocalSearch = vendorData.some(vendor => 'approximateMiles' in vendor && vendor.approximateMiles);

  // Determine vendor type for filename and orientation
  const state: RootState = store.getState();
  const vendorType = state.providerSearch.currentSearchCategory || 'vendor';  
  const externalSearchTypeState = (state.providerSearch as any).externalSearchType as ('local' | 'name' | 'region' | undefined);
  // Explicitly type the orientation as "portrait" | "landscape" to match the expected type
  let pdfOrientation: "portrait" | "landscape" = 'portrait';
  
  if (vendorData.length > 0) {
    if ( vendorType.toLowerCase() === 'property' ) {
      pdfOrientation = 'landscape';
    }
  }

  const effectiveExternalSearchType = options.externalSearchType ?? externalSearchTypeState;

  await exportVendorsToPDF(vendorData, {
    ...options,
    title: transformTitle(options.title || 'Selected Vendors'),
    filename: options.filename || `selected-${vendorType}-vendors.pdf`,
    orientation: pdfOrientation,
    isLocalSearch,
    ...(effectiveExternalSearchType ? { externalSearchType: effectiveExternalSearchType } : {}),
  });
};


export default exportVendorsToPDF;

